import React, { useEffect, useState, useMemo } from "react";
import {
    CircularProgress,
    Button,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Avatar,
    Paper,
    Typography,
    Menu,
    Checkbox,
    ListItemText
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import FilterListIcon       from "@mui/icons-material/FilterList";
import ClearIcon            from "@mui/icons-material/Clear";
import VisibilityIcon       from "@mui/icons-material/Visibility";
import ArrowUpwardIcon      from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon    from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon       from "@mui/icons-material/UnfoldMore";
import ViewColumnIcon       from "@mui/icons-material/ViewColumn";
import { useSnackbar } from "notistack";
import moment from "moment/moment";
import { baseUrl } from "../../config/config";
import { get_member_list_api, get_table_preferences_api, update_table_preferences_api } from "../../DAL/Passenger/Passenger";
import UserDetailDrawer from "../../components/UserDetailDrawer";
import * as XLSX from "xlsx";

// ── Column visibility ─────────────────────────────────────────────────────────
// "User" (name) is always shown as the row's identity anchor; everything else
// here is optional and persisted per-admin via the table-preferences API.
const COLUMN_DEFS = [
    { key: "email",      label: "Email" },
    { key: "plan",       label: "Plan" },
    { key: "joined",     label: "Joined" },
    { key: "lastLogin",  label: "Last Login" },
    { key: "spend",      label: "Spend" },
    { key: "credits",    label: "Credits" },
    { key: "questions",  label: "Questions" },
    { key: "status",     label: "Status" },
];
const DEFAULT_VISIBLE_COLUMNS = COLUMN_DEFS.map((c) => c.key);
const TABLE_KEY = "passengers";

// Default column widths (px) — user-resizable, persisted per-admin alongside visibility.
const DEFAULT_COLUMN_WIDTHS = {
    name:          240,
    email:         220,
    plan:          100,
    joined:        120,
    lastLogin:     120,
    spend:         90,
    credits:       90,
    questions:     100,
    status:        100,
};

// ── Shared input styles ───────────────────────────────────────────────────────

const inputSx = {
    "& .MuiOutlinedInput-root": {
        backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a", fontSize: "14px",
        "& fieldset": { borderColor: "rgba(26,26,26,0.16)" },
        "&:hover fieldset": { borderColor: "rgba(26,26,26,0.28)" },
        "&.Mui-focused fieldset": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    },
    "& .MuiInputBase-input": { color: "#1a1a1a" },
    "& .MuiInputLabel-root": { color: "#aaa", fontSize: "14px" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#c01e2e" },
};

const selectSx = {
    backgroundColor: "#ffffff", borderRadius: "9px", color: "#1a1a1a", fontSize: "14px",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.16)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(26,26,26,0.28)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#c01e2e", borderWidth: "1.5px" },
    "& .MuiSvgIcon-root": { color: "#c01e2e" },
};

// ── Sort helpers ──────────────────────────────────────────────────────────────

const SORT_FIELDS = {
    name:              (u) => (u.name || "").toLowerCase(),
    email:             (u) => (u.email || "").toLowerCase(),
    paymentPlan:       (u) => (u.paymentPlan || "").toLowerCase(),
    auth_provider:     (u) => (u.auth_provider || "").toLowerCase(),
    createdAt:         (u) => new Date(u.createdAt || 0).getTime(),
    lastLogin:         (u) => new Date(u.lastLogin || 0).getTime(),
    status:            (u) => (u.status ? 1 : 0),
    totalQuestions:    (u) => u.totalQuestions || 0,
    creditsSpent:      (u) => u.lifetimeRevenue || 0,
    creditBalance:     (u) => u.tokenBalance ?? 0,
};

const applySortAndFilter = (list, filters, sort) => {
    const { searchQuery, statusFilter, paymentPlanFilter } = filters;
    const filtered = list.filter((user) => {
        const s = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery ||
            user.name?.toLowerCase().includes(s) ||
            user.email?.toLowerCase().includes(s) ||
            user.phone_number?.toLowerCase().includes(s);
        const matchesStatus = statusFilter === "all" ||
            (statusFilter === "active" && user.status === true) ||
            (statusFilter === "inactive" && user.status === false);
        const matchesPlan = paymentPlanFilter === "all" || user.paymentPlan === paymentPlanFilter;
        return matchesSearch && matchesStatus && matchesPlan;
    });

    if (!sort.field) return filtered;
    const getter = SORT_FIELDS[sort.field];
    return [...filtered].sort((a, b) => {
        const av = getter(a), bv = getter(b);
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
    });
};

// ── Sort header cell ──────────────────────────────────────────────────────────

function ResizeHandle({ columnKey, onResizeStart }) {
    if (!onResizeStart) return null;
    return (
        <Box
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onResizeStart(columnKey, e); }}
            onClick={(e) => e.stopPropagation()}
            sx={{
                position: "absolute", top: 0, right: 0, height: "100%", width: "6px",
                cursor: "col-resize", userSelect: "none", zIndex: 1,
                "&:hover": { backgroundColor: "rgba(192,30,46,0.3)" },
            }}
        />
    );
}

function SortHeader({ label, field, sort, onSort, width, columnKey, onResizeStart }) {
    const active = sort.field === field;
    return (
        <TableCell
            onClick={() => onSort(field)}
            sx={{
                position: "relative",
                color: active ? "#c01e2e" : "#6b6b6b",
                fontWeight: 600,
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                fontFamily: "'Rubik', sans-serif",
                borderBottom: "1px solid rgba(26,26,26,0.09)",
                backgroundColor: "#F2EDE4",
                cursor: "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                ...(width && { width, minWidth: width, maxWidth: width }),
                "&:hover": { color: "#c01e2e", "& .sort-icon": { opacity: 1 } },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {label}
                <Box className="sort-icon" sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    ml: 0.25,
                    opacity: active ? 1 : 0.5,
                    transition: "opacity 0.15s ease",
                }}>
                    <ArrowUpwardIcon sx={{
                        fontSize: 9,
                        display: "block",
                        color: active && sort.dir === "asc" ? "#c01e2e" : "inherit",
                        mb: "-2px",
                    }} />
                    <ArrowDownwardIcon sx={{
                        fontSize: 9,
                        display: "block",
                        color: active && sort.dir === "desc" ? "#c01e2e" : "inherit",
                    }} />
                </Box>
            </Box>
            <ResizeHandle columnKey={columnKey} onResizeStart={onResizeStart} />
        </TableCell>
    );
}

function PlainHeader({ label, width, columnKey, onResizeStart }) {
    return (
        <TableCell sx={{
            position: "relative",
            color: "#6b6b6b", fontWeight: 600, fontSize: "11px",
            textTransform: "uppercase", letterSpacing: "0.4px",
            fontFamily: "'Rubik', sans-serif",
            borderBottom: "1px solid rgba(26,26,26,0.09)",
            backgroundColor: "#F2EDE4",
            ...(width && { width, minWidth: width, maxWidth: width }),
        }}>
            {label}
            <ResizeHandle columnKey={columnKey} onResizeStart={onResizeStart} />
        </TableCell>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PassengersList() {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading,       setIsLoading]       = useState(true);
    const [passengersList,  setPassengersList]  = useState([]);
    const [searchQuery,     setSearchQuery]     = useState("");
    const [statusFilter,    setStatusFilter]    = useState("all");
    const [paymentPlanFilter, setPaymentPlanFilter] = useState("all");
    const [page,            setPage]            = useState(0);
    const [rowsPerPage,     setRowsPerPage]     = useState(25);
    const [selectedUser,    setSelectedUser]    = useState(null);
    const [drawerOpen,      setDrawerOpen]      = useState(false);
    const [sort,            setSort]            = useState({ field: "createdAt", dir: "desc" });
    const [visibleColumns,  setVisibleColumns]  = useState(DEFAULT_VISIBLE_COLUMNS);
    const [columnWidths,    setColumnWidths]    = useState(DEFAULT_COLUMN_WIDTHS);
    const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);

    useEffect(() => { getPassengerList(); loadColumnPreferences(); }, []);

    const loadColumnPreferences = async () => {
        try {
            const result = await get_table_preferences_api();
            const saved = result?.tablePreferences?.[TABLE_KEY];
            if (Array.isArray(saved)) {
                // Legacy shape — just an array of visible column keys, no widths saved yet.
                setVisibleColumns(saved);
            } else if (saved && typeof saved === "object") {
                if (Array.isArray(saved.visibleColumns)) setVisibleColumns(saved.visibleColumns);
                if (saved.columnWidths && typeof saved.columnWidths === "object") {
                    setColumnWidths((prev) => ({ ...prev, ...saved.columnWidths }));
                }
            }
        } catch {
            // Preference load failing just falls back to showing every column at default widths.
        }
    };

    const persistPreferences = (columns, widths) => {
        update_table_preferences_api(TABLE_KEY, { visibleColumns: columns, columnWidths: widths }).catch(() => {});
    };

    const toggleColumn = (key) => {
        setVisibleColumns((prev) => {
            const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
            persistPreferences(next, columnWidths);
            return next;
        });
    };

    // Drag-to-resize — handlers are created fresh per drag so add/removeEventListener
    // always reference the same function instance, avoiding stale-closure mismatches.
    const handleResizeStart = (key, e) => {
        const startX = e.clientX;
        const startWidth = columnWidths[key] || DEFAULT_COLUMN_WIDTHS[key] || 120;

        const onMove = (moveEvent) => {
            const newWidth = Math.max(60, startWidth + (moveEvent.clientX - startX));
            setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
        };

        const onUp = () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
            setColumnWidths((prev) => {
                persistPreferences(visibleColumns, prev);
                return prev;
            });
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const getPassengerList = async () => {
        try {
            const result = await get_member_list_api();
            if (result.code === 200) {
                setPassengersList(result.member_list.map((m) => ({
                    ...m,
                    name: m.first_name + " " + m.last_name,
                    paymentPlan: m.paymentPlan || "None",
                })));
                setIsLoading(false);
            } else {
                enqueueSnackbar(result.message, { variant: "error" });
                setIsLoading(false);
            }
        } catch {
            enqueueSnackbar("Error fetching member list", { variant: "error" });
            setIsLoading(false);
        }
    };

    const paymentPlans = useMemo(() => (
        [...new Set(passengersList.map(p => p.paymentPlan).filter(Boolean))].sort()
    ), [passengersList]);

    const filteredData = useMemo(() =>
        applySortAndFilter(passengersList, { searchQuery, statusFilter, paymentPlanFilter }, sort),
        [passengersList, searchQuery, statusFilter, paymentPlanFilter, sort]
    );

    const hasActiveFilters = searchQuery !== "" || statusFilter !== "all" || paymentPlanFilter !== "all";

    const handleSort = (field) => {
        setPage(0);
        setSort((prev) =>
            prev.field === field
                ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
                : { field, dir: "asc" }
        );
    };

    const clearFilters = () => { setSearchQuery(""); setStatusFilter("all"); setPaymentPlanFilter("all"); };
    const handleRowClick = (user) => { setSelectedUser(user); setDrawerOpen(true); };
    const handleChangePage = (_, p) => setPage(p);
    const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

    const exportToExcel = () => {
        const ws = XLSX.utils.json_to_sheet(filteredData.map(u => ({
            Name: u.name, Email: u.email, Phone: u.phone_number,
            "Payment Plan": u.paymentPlan, "Auth Provider": u.auth_provider || "local",
            Status: u.status ? "Active" : "Inactive",
            "Questions": u.totalQuestions || 0,
            "Joined": u.createdAt ? moment(u.createdAt).format("DD-MM-YYYY") : "",
            "Last Login": u.lastLogin ? moment(u.lastLogin).format("DD-MM-YYYY HH:mm") : "",
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Members");
        XLSX.writeFile(wb, "members_list.xlsx");
    };

    return (
        <>
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                    <CircularProgress sx={{ color: "#c01e2e" }} />
                </Box>
            ) : (
                <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "calc(100vh - 48px)" }}>
                    <Paper sx={{ backgroundColor: "#ffffff", border: "1px solid rgba(26,26,26,0.09)", borderRadius: "14px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", p: 3 }}>

                        {/* Header */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, pb: 2.5, borderBottom: "1px solid rgba(26,26,26,0.09)" }}>
                            <Box>
                                <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1a1a1a", fontFamily: "'Rubik', sans-serif" }}>Members</Typography>
                                <Typography sx={{ fontSize: "13px", color: "#aaa", fontFamily: "'Rubik', sans-serif", mt: 0.25 }}>
                                    {passengersList.length} total platform users
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Button variant="outlined" onClick={(e) => setColumnsMenuAnchor(e.currentTarget)} startIcon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
                                    sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, borderRadius: "8px", px: 2, boxShadow: "none",
                                        "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" } }}>
                                    Columns
                                </Button>
                                <Menu anchorEl={columnsMenuAnchor} open={Boolean(columnsMenuAnchor)} onClose={() => setColumnsMenuAnchor(null)}>
                                    {COLUMN_DEFS.map((col) => (
                                        <MenuItem key={col.key} onClick={() => toggleColumn(col.key)} dense
                                            sx={{ fontFamily: "'Rubik', sans-serif" }}>
                                            <Checkbox checked={visibleColumns.includes(col.key)} size="small"
                                                sx={{ color: "rgba(26,26,26,0.3)", "&.Mui-checked": { color: "#c01e2e" }, p: 0.5, mr: 1 }} />
                                            <ListItemText primaryTypographyProps={{ fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                {col.label}
                                            </ListItemText>
                                        </MenuItem>
                                    ))}
                                </Menu>
                                <Button variant="contained" onClick={exportToExcel}
                                    sx={{ backgroundColor: "#c01e2e", color: "#fff", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", fontWeight: 500, borderRadius: "8px", px: 2.5, boxShadow: "none", "&:hover": { backgroundColor: "#a81828", boxShadow: "none" } }}>
                                    Export to Excel
                                </Button>
                            </Box>
                        </Box>

                        {/* Filters */}
                        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                            <TextField size="small" placeholder="Search by name, email, phone…"
                                value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ ...inputSx, minWidth: 260 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#aaa", fontSize: 18 }} /></InputAdornment> }} />

                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Status</InputLabel>
                                <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)} sx={selectSx}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel sx={{ color: "#aaa", fontSize: "14px", "&.Mui-focused": { color: "#c01e2e" } }}>Plan</InputLabel>
                                <Select value={paymentPlanFilter} label="Plan" onChange={(e) => setPaymentPlanFilter(e.target.value)} sx={selectSx}>
                                    <MenuItem value="all">All Plans</MenuItem>
                                    {paymentPlans.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </Select>
                            </FormControl>

                            {hasActiveFilters && (
                                <Button variant="outlined" size="small" onClick={clearFilters} startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                                    sx={{ borderColor: "rgba(26,26,26,0.2)", color: "#6b6b6b", textTransform: "none", fontFamily: "'Rubik', sans-serif", fontSize: "13px", borderRadius: "8px",
                                        "&:hover": { borderColor: "#c01e2e", color: "#c01e2e", backgroundColor: "rgba(192,30,46,0.06)" } }}>
                                    Clear
                                </Button>
                            )}

                            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.75 }}>
                                <FilterListIcon sx={{ color: "#aaa", fontSize: 16 }} />
                                <Typography sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                    {filteredData.length} of {passengersList.length}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Active filter chips */}
                        {hasActiveFilters && (
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                                {searchQuery && <Chip label={`Search: "${searchQuery}"`} onDelete={() => setSearchQuery("")} size="small"
                                    sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "#c01e2e" } }} />}
                                {statusFilter !== "all" && <Chip label={`Status: ${statusFilter}`} onDelete={() => setStatusFilter("all")} size="small"
                                    sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "#c01e2e" } }} />}
                                {paymentPlanFilter !== "all" && <Chip label={`Plan: ${paymentPlanFilter}`} onDelete={() => setPaymentPlanFilter("all")} size="small"
                                    sx={{ backgroundColor: "rgba(192,30,46,0.08)", color: "#c01e2e", fontFamily: "'Rubik', sans-serif", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "#c01e2e" } }} />}
                            </Box>
                        )}

                        {/* Table */}
                        <TableContainer component={Paper} sx={{ backgroundColor: "transparent", boxShadow: "none", overflowX: "auto" }}>
                            <Table sx={{ tableLayout: "fixed" }}>
                                <TableHead>
                                    <TableRow>
                                        <PlainHeader label="#" width={48} />
                                        <SortHeader label="User"          field="name"           sort={sort} onSort={handleSort} width={columnWidths.name} columnKey="name" onResizeStart={handleResizeStart} />
                                        {visibleColumns.includes("email")     && <SortHeader label="Email"      field="email"          sort={sort} onSort={handleSort} width={columnWidths.email}     columnKey="email"     onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("plan")      && <SortHeader label="Plan"       field="paymentPlan"    sort={sort} onSort={handleSort} width={columnWidths.plan}      columnKey="plan"      onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("joined")    && <SortHeader label="Joined"     field="createdAt"      sort={sort} onSort={handleSort} width={columnWidths.joined}    columnKey="joined"    onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("lastLogin") && <SortHeader label="Last Login" field="lastLogin"      sort={sort} onSort={handleSort} width={columnWidths.lastLogin} columnKey="lastLogin" onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("spend")     && <SortHeader label="Spend"      field="creditsSpent"   sort={sort} onSort={handleSort} width={columnWidths.spend}     columnKey="spend"     onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("credits")   && <SortHeader label="Credits"    field="creditBalance"  sort={sort} onSort={handleSort} width={columnWidths.credits}   columnKey="credits"   onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("questions") && <SortHeader label="Questions"  field="totalQuestions" sort={sort} onSort={handleSort} width={columnWidths.questions} columnKey="questions" onResizeStart={handleResizeStart} />}
                                        {visibleColumns.includes("status")    && <SortHeader label="Status"     field="status"         sort={sort} onSort={handleSort} width={columnWidths.status}    columnKey="status"    onResizeStart={handleResizeStart} />}
                                        <PlainHeader label="" width={40} />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user, index) => (
                                        <TableRow key={user._id || index} hover onClick={() => handleRowClick(user)}
                                            sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#F7F4EF" }, "& td": { borderBottom: "1px solid rgba(26,26,26,0.06)" } }}>

                                            <TableCell sx={{ color: "#aaa", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>

                                            <TableCell sx={{ width: columnWidths.name, maxWidth: columnWidths.name, overflow: "hidden" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                                    <Avatar src={user.profile_image ? baseUrl + user.profile_image : undefined}
                                                        sx={{ width: 34, height: 34, backgroundColor: "#c01e2e", fontSize: "13px", flexShrink: 0 }}>
                                                        {user.first_name?.[0]}{user.last_name?.[0]}
                                                    </Avatar>
                                                    <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                                                        <Box sx={{ color: "#1a1a1a", fontSize: "14px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.name}</Box>
                                                        {user.phone_number && <Box sx={{ color: "#aaa", fontSize: "12px", fontFamily: "'Rubik', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.phone_number}</Box>}
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {visibleColumns.includes("email") && (
                                                <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif", width: columnWidths.email, maxWidth: columnWidths.email, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</TableCell>
                                            )}

                                            {visibleColumns.includes("plan") && (
                                                <TableCell>
                                                    <Chip label={user.paymentPlan} size="small"
                                                        sx={{ backgroundColor: "rgba(192,30,46,0.09)", color: "#c01e2e", fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
                                                </TableCell>
                                            )}

                                            {visibleColumns.includes("joined") && (
                                                <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                    {user.createdAt ? moment(user.createdAt).format("DD MMM YYYY") : "—"}
                                                </TableCell>
                                            )}

                                            {visibleColumns.includes("lastLogin") && (
                                                <TableCell sx={{ color: "#6b6b6b", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>
                                                    {user.lastLogin ? moment(user.lastLogin).fromNow() : "—"}
                                                </TableCell>
                                            )}

                                            {/* Spend — lifetime revenue (subscriptions, top-ups, credits, everything) */}
                                            {visibleColumns.includes("spend") && (
                                                <TableCell sx={{ fontFamily: "'Rubik', sans-serif" }}>
                                                    {(() => {
                                                        const spent = user.lifetimeRevenue || 0;
                                                        return spent > 0 ? (
                                                            <Typography sx={{ color: "#1a1a1a", fontSize: "13px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                                                                £{spent.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                            </Typography>
                                                        ) : (
                                                            <Typography sx={{ color: "#ccc", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>—</Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}

                                            {/* Credit balance — current token balance (UserTokens.balance), not the legacy explanationCredits field */}
                                            {visibleColumns.includes("credits") && (
                                                <TableCell sx={{ fontFamily: "'Rubik', sans-serif" }}>
                                                    {(() => {
                                                        const bal = user.tokenBalance ?? null;
                                                        if (bal === null || bal === undefined) return <Typography sx={{ color: "#ccc", fontSize: "13px", fontFamily: "'Rubik', sans-serif" }}>—</Typography>;
                                                        return (
                                                            <Typography sx={{ color: bal > 0 ? "#16a34a" : "#aaa", fontSize: "13px", fontWeight: bal > 0 ? 600 : 400, fontFamily: "'Rubik', sans-serif" }}>
                                                                {bal.toLocaleString()}
                                                            </Typography>
                                                        );
                                                    })()}
                                                </TableCell>
                                            )}

                                            {visibleColumns.includes("questions") && (
                                                <TableCell sx={{ color: "#1a1a1a", fontSize: "13px", fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                                                    {user.totalQuestions ? user.totalQuestions.toLocaleString() : "—"}
                                                </TableCell>
                                            )}

                                            {visibleColumns.includes("status") && (
                                                <TableCell>
                                                    <Chip label={user.status ? "Active" : "Inactive"} size="small"
                                                        sx={{ backgroundColor: user.status ? "rgba(22,163,74,0.1)" : "rgba(192,30,46,0.1)",
                                                            color: user.status ? "#16a34a" : "#c01e2e", fontSize: "11px", fontWeight: 500, fontFamily: "'Rubik', sans-serif", borderRadius: "6px" }} />
                                                </TableCell>
                                            )}

                                            <TableCell><VisibilityIcon sx={{ color: "#aaa", fontSize: 18 }} /></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination component="div" count={filteredData.length} page={page}
                            onPageChange={handleChangePage} rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[10, 25, 50, 100]}
                            sx={{ color: "#6b6b6b", fontFamily: "'Rubik', sans-serif",
                                "& .MuiTablePagination-selectIcon": { color: "#c01e2e" },
                                "& .MuiTablePagination-actions button": { color: "#6b6b6b" } }} />
                    </Paper>
                </Box>
            )}

            <UserDetailDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} user={selectedUser} />
        </>
    );
}
